'use client';
import type { DetailNavigationTabProps } from './data';

import React, { useEffect, useState } from 'react';
import parse from 'html-react-parser';

import Comments from '../Comments';
import DetailsNavigationLayout from '../DetailsNavigationLayout';
import ProductDetailsTableRow from '../ProductDetailsTableRow';
import ProductFAQ from '../ProductFAQ';
import ProductVideo from '../ProductVideo';

import { Box, Container, Link, Stack, Tab, TabList, Tabs } from '@/components';
import { useTranslation } from '@/hooks';
import { fonts } from '@/theme';

const DetailsNavigationTab: DetailNavigationTabProps = ({
  productData,
  pagination,
  comments,
  galleryImages,
  faqs,
}) => {
  const { t } = useTranslation('pages.products');
  const [activeTab, setActiveTab] = useState('product-description');

  const tabs = [
    {
      id: 'product-description',
      title: t('description'),
    },
    {
      id: 'product-details',
      title: t('details'),
    },
    {
      id: 'video',
      title: t('video'),
      isHidden: !productData.video_iframe,
    },
    {
      id: 'comments',
      title: t('comments'),
    },
    {
      id: 'faqs',
      title: t('questions'),
      isHidden: faqs.length === 0,
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;

      const sectionIds = [
        'product-description',
        'product-details',
        'video',
        'comments',
        'faqs',
      ];

      const activeSection = sectionIds.find((sectionId) => {
        const element = document.getElementById(sectionId);

        if (element) {
          const elementTop = element.offsetTop - 100;
          const elementBottom = elementTop + element.offsetHeight;

          return scrollPosition >= elementTop && scrollPosition < elementBottom;
        }

        return false;
      });

      if (activeSection) {
        setActiveTab(activeSection);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  const MemoizedProductVideo = React.useMemo(
    () => <ProductVideo videoIframe={productData.video_iframe} />,
    []
  );

  const memoizedActiveTabIndex = React.useMemo(
    () => tabs.findIndex((tab) => tab.id === activeTab),
    [activeTab]
  );

  return (
    <>
      <Container mt="8">
        <Tabs
          index={memoizedActiveTabIndex}
          color="primary.400"
          borderRadius={16}
          bg="gray.250"
          pos="sticky"
          top={0}
          zIndex={9}
        >
          <TabList
            border="none"
            role="tablist"
            gap={2.5}
            h={{ base: '34px', md: '46px' }}
            justifyContent={{ base: 'space-between', md: 'flex-start' }}
          >
            {tabs.map(
              (tab) =>
                !tab.isHidden && (
                  <Tab
                    key={tab.id}
                    id={`tab-${tab.id}`}
                    role="tab"
                    aria-controls={`panel-${tab.id}`}
                    aria-selected={activeTab === tab.id}
                    px={{ base: 2, md: 8 }}
                    fontSize={{ base: 'xs', md: 'md' }}
                    fontWeight={700}
                    borderRadius={16}
                    _selected={{
                      color: 'gray.800',
                      bgColor: 'secondary.500',
                    }}
                    w={{ base: '25%', md: 'max-content' }}
                    minW="max-content"
                    py="8px"
                  >
                    <Link href={`#${tab.id}`} replace shallow prefetch={false}>
                      {tab.title}
                    </Link>
                  </Tab>
                )
            )}
          </TabList>
        </Tabs>
        <DetailsNavigationLayout
          id="product-description"
          title={t('description')}
          content={
            <Box
              mx={{ base: 2, md: '22px' }}
              sx={{
                '*': { fontFamily: `${fonts.style.fontFamily} !important` },
                color: 'black',
              }}
            >
              {parse(productData.description)}
            </Box>
          }
        />
        <DetailsNavigationLayout
          id="product-details"
          title={t('details')}
          content={
            <Stack gap="0">
              <ProductDetailsTableRow
                title={t(
                  productData.measurement_type === 'gr' ? 'weight' : 'volume'
                )}
                value={`${productData.measurement_value} ${t(productData.measurement_type)}`}
              />
              {productData.how_to_use && productData.how_to_use !== '.' && (
                <ProductDetailsTableRow
                  title={t('howToUse')}
                  value={productData.how_to_use}
                />
              )}
              {productData.attributes.map((attr) => (
                <ProductDetailsTableRow
                  key={attr.key}
                  title={attr.key}
                  value={attr.value}
                />
              ))}
            </Stack>
          }
        />
        {productData.video_iframe && (
          <Container>
            <DetailsNavigationLayout
              id="video"
              title={t('video')}
              content={MemoizedProductVideo}
            />
          </Container>
        )}
        <DetailsNavigationLayout
          id="comments"
          title={t('comments')}
          content={
            <Comments
              productGroupCode={productData.code}
              pagination={pagination}
              comments={comments}
              galleryImages={galleryImages}
            />
          }
        />
      </Container>
      <Container px={{ base: '0px !important', md: '1.5rem !important' }}>
        {faqs.length > 0 && (
          <DetailsNavigationLayout
            id="faqs"
            title={t('questions')}
            content={<ProductFAQ faqs={faqs} />}
          />
        )}
      </Container>
    </>
  );
};
export default DetailsNavigationTab;
